import { z } from 'zod';
import type { IssueSeverity } from './vocabularies';
import {
  AllocationBasis,
  ConfidenceBand,
  ConfidenceMethod,
  MappingSetStatus,
  RelationshipRole,
  RelationshipStatus,
} from './vocabularies';
import { IsoDate, Timestamp, Uuid } from './envelope';
import { EntityType } from './entities';

/**
 * The relationship kernel.
 *
 * Source: Relationship Model §2 (record envelope), §3 (76 typed relationships in
 * 15 categories), §6 (mapping-set lifecycle) and §10 (validation rules).
 *
 * The premise of the whole file is the model's core rule: a line drawn on a
 * mapping canvas is never presentation. Every edge is a typed, effective-dated,
 * governed record with endpoints, role, confidence, evidence and status. That is
 * why relationships get their own kernel rather than being foreign keys — a
 * foreign key cannot be proposed, evidenced, disputed, approved or superseded.
 */

// ── Relationship types ───────────────────────────────────────────────────────

/**
 * The 76 catalog entries collapse to 74 distinct names: `CONTAINS` is used by
 * both REL-H001 (Enterprise→OrganizationalUnit) and REL-H002 (unit→unit), and
 * `AFFECTS_ENTITY` by both REL-L006 (Quality) and REL-D010 (Risk). The name
 * alone therefore does not identify a catalog row — RELATIONSHIP_CATALOG below
 * is what distinguishes them, by endpoint pair.
 */
export const RelationshipType = z.enum([
  // Association
  'ALIGNED_TO_VALUE_STREAM',
  'SERVES_CUSTOMER',
  'DELIVERS_OUTCOME',
  'MEASURED_BY',
  'HAS_OUTCOME',
  // Authorization
  'HAS_ACCESS_SCOPE',
  'APPLIES_TO_NODE',
  // Composition
  'INCLUDES_PROCESS',
  'INCLUDES_PROCESS_STEP',
  'SUPPORTED_BY_APPLICATION',
  'SUPPORTED_BY_SERVICE',
  'CONSUMES_API',
  'USES_DATA_ASSET',
  'CONSUMES_DATA_PRODUCT',
  'IMPLEMENTS_PROCESS',
  'IMPLEMENTS_PROCESS_STEP',
  'EXPOSES_SERVICE',
  'EXPOSES_API',
  'STORES_OR_PROCESSES',
  'BUILT_FROM',
  'HAS_BOUNDARY',
  'INCLUDES_ENTITY',
  // Delivery
  'ASSIGNED_TO_TEAM',
  'ADVANCES_PRODUCT',
  'TARGETS_OUTCOME',
  'DELIVERS_WORK_ITEM',
  'RELEASES_PRODUCT',
  'IMPLEMENTS_RELEASE',
  // Dependency
  'DEPENDS_ON_PRODUCT',
  'DEPENDS_ON_APPLICATION',
  'DEPENDS_ON_SERVICE',
  'CALLS_API',
  // Finance
  'FUNDS_ENTITY',
  'COST_OF_ENTITY',
  'ALLOCATES_COST_TO',
  'REALIZED_BY_PRODUCT',
  'SUPPORTED_BY_RELEASE',
  // Governance
  'SUPPORTS_ENTITY',
  'DECIDES_ENTITY',
  'APPROVES_DECISION',
  'FOLLOWS_FROM',
  'COMMENTS_ON',
  // Hierarchy (CONTAINS covers REL-H001 and REL-H002)
  'CONTAINS',
  'OWNS_VALUE_STREAM',
  'CONTAINS_PRODUCT_GROUP',
  'CONTAINS_PRODUCT',
  'DIRECTLY_CONTAINS_PRODUCT',
  'HAS_SUBPRODUCT',
  // Lineage
  'PRODUCES_RUN',
  'INGESTS_SOURCE_RECORD',
  'MAPS_TO_ENTITY',
  'GENERATES_LINEAGE',
  // Ownership
  'OWNS_PRODUCT',
  'OWNS_PROCESS',
  'OWNS_APPLICATION',
  'OWNS_DATA',
  'OWNS_METRIC',
  'DELIVERS_PRODUCT',
  'MEMBER_OF_TEAM',
  // Quality (AFFECTS_ENTITY also serves Risk / REL-D010)
  'RAISES_ISSUE',
  'AFFECTS_ENTITY',
  // Reliability
  'IMPACTS_PRODUCT',
  'IMPACTS_SERVICE',
  'CHANGES_APPLICATION',
  // Scenario
  'HAS_ASSUMPTION',
  'HAS_RESULT',
  'MODELS_ENTITY',
  'SELECTS_SCENARIO',
  // Sourcing
  'SUPPLIES_PRODUCT',
  'GOVERNS_SUPPLIER',
  'SUPPORTS_PRODUCT',
  'UNDER_CONTRACT',
  'DELIVERS_FOR_PRODUCT',
  'HAS_OBLIGATION',
]);
export type RelationshipType = z.infer<typeof RelationshipType>;

export const RelationshipCategory = z.enum([
  'Association',
  'Authorization',
  'Composition',
  'Delivery',
  'Dependency',
  'Finance',
  'Governance',
  'Hierarchy',
  'Lineage',
  'Ownership',
  'Quality',
  'Reliability',
  'Risk',
  'Scenario',
  'Sourcing',
]);
export type RelationshipCategory = z.infer<typeof RelationshipCategory>;

export const RelationshipCardinality = z.enum(['1:N', 'N:1', 'N:M']);
export type RelationshipCardinality = z.infer<typeof RelationshipCardinality>;

// ── Relationship record envelope ─────────────────────────────────────────────

export const RelationshipEnvelope = z.object({
  relationshipId: Uuid,
  relationshipType: RelationshipType,

  fromEntityType: EntityType,
  fromEntityId: Uuid,
  toEntityType: EntityType,
  toEntityId: Uuid,

  /**
   * Conditional in the specification, required by several types (see
   * `requiresRole` in the catalog). Primary versus Supporting is what makes a
   * many-to-many alignment answerable — without it, "which value stream owns
   * this product" has no answer and every roll-up double counts.
   */
  role: RelationshipRole.nullable(),

  /** 0–1. Null means unknown, which is not the same as zero and never implies an equal split. */
  allocationPercent: z.number().min(0).max(1).nullable(),
  allocationBasis: AllocationBasis.nullable(),

  confidenceScore: z.number().min(0).max(1).nullable(),
  confidenceBand: ConfidenceBand.nullable(),
  /**
   * Held separately from the band because governance status and source accuracy
   * are different things: approval does not make poor source data correct
   * (Relationship Model §7).
   */
  confidenceMethod: ConfidenceMethod.nullable(),

  evidenceId: Uuid.nullable(),
  status: RelationshipStatus,

  /**
   * Business effective interval. The specification types these as timestamps;
   * they are dates here so that as-of traversal uses the same clock as the entity
   * envelope — a relationship that changes at noon still belongs to one reporting
   * day, and mixing the two granularities makes historical views irreproducible.
   */
  validFrom: IsoDate,
  validTo: IsoDate.nullable(),

  /** Which governed release published this edge. Null while it is a loose proposal. */
  mappingSetId: Uuid.nullable(),
  mappingSetVersion: z.number().int().positive().nullable(),

  sourceSystem: z.string().min(1).nullable(),
  sourceRecordId: z.string().min(1).nullable(),

  rowVersion: z.number().int().nonnegative(),

  createdAt: Timestamp,
  createdBy: Uuid,
  updatedAt: Timestamp,
  updatedBy: Uuid,
  /** Null until approved. Separate from updatedBy so RM-015 self-approval is detectable. */
  approvedAt: Timestamp.nullable(),
  approvedBy: Uuid.nullable(),

  /** Governed extension. Never core semantics — a new meaning needs a new type. */
  attributes: z.record(z.unknown()).nullable(),
});
export type RelationshipEnvelope = z.infer<typeof RelationshipEnvelope>;

// ── Endpoint catalog ─────────────────────────────────────────────────────────

/**
 * Endpoint wildcard.
 *
 * The catalog describes some endpoints as "any governed entity", "any canonical
 * entity" or "any costable entity" — evidence, decisions, comments, risks, DQ
 * issues, source-record lineage and cost subjects all attach to almost anything.
 * Enumerating all 61 types for those rows would encode a closed list that the
 * specification deliberately left open, and it would need editing every time an
 * entity is added. The sentinel keeps the openness explicit and forces callers
 * through `isValidEndpointPair`, which is the only place that has to know.
 */
export const ANY_ENTITY = '*';

export interface RelationshipCatalogEntry {
  /** REL- code from the catalog. Unique; the type name is not. */
  code: string;
  category: RelationshipCategory;
  type: RelationshipType;
  /** Allowed source entity types, or `['*']` for any governed entity. */
  fromTypes: readonly string[];
  toTypes: readonly string[];
  cardinality: RelationshipCardinality;
  requiresRole: boolean;
  requiresEvidence: boolean;
  allowsAllocation: boolean;
  primaryUse: string;
}

/**
 * All 76 catalog rows.
 *
 * `requiresRole` / `requiresEvidence` / `allowsAllocation` are read from each
 * row's Constraints column. They are policy defaults: RM-005 lets a tenant
 * require evidence more widely, never less.
 *
 * Two endpoint groups in the source are named by concept rather than by entity
 * and are resolved here (both noted in the accompanying report):
 *  - REL-F003 "SharedService" is a classification class, not an entity; the
 *    entity that carries it is Service.
 *  - REL-C003 "Portfolio" is not an entity either; the portfolio boundary is
 *    Enterprise.
 */
export const RELATIONSHIP_CATALOG: readonly RelationshipCatalogEntry[] = [
  // Association
  {
    code: 'REL-A001',
    category: 'Association',
    type: 'ALIGNED_TO_VALUE_STREAM',
    fromTypes: ['Product'],
    toTypes: ['ValueStream'],
    cardinality: 'N:M',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Cross-value-stream alignment',
  },
  {
    code: 'REL-A002',
    category: 'Association',
    type: 'SERVES_CUSTOMER',
    fromTypes: ['Product'],
    toTypes: ['CustomerSegment'],
    cardinality: 'N:M',
    requiresRole: true,
    requiresEvidence: true,
    allowsAllocation: false,
    primaryUse: 'Product mission',
  },
  {
    code: 'REL-A003',
    category: 'Association',
    type: 'DELIVERS_OUTCOME',
    fromTypes: ['Product'],
    toTypes: ['Outcome'],
    cardinality: 'N:M',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Outcome traceability',
  },
  {
    code: 'REL-A004',
    category: 'Association',
    type: 'MEASURED_BY',
    fromTypes: ['Outcome'],
    toTypes: ['MetricDefinition'],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'KPI catalog',
  },
  {
    code: 'REL-A005',
    category: 'Association',
    type: 'HAS_OUTCOME',
    fromTypes: ['OKR'],
    toTypes: ['Outcome'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'OKR traceability',
  },

  // Authorization
  {
    code: 'REL-O008',
    category: 'Authorization',
    type: 'HAS_ACCESS_SCOPE',
    fromTypes: ['Person'],
    toTypes: ['AccessScope'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Authorization',
  },
  {
    code: 'REL-O009',
    category: 'Authorization',
    type: 'APPLIES_TO_NODE',
    fromTypes: ['AccessScope'],
    // "Hierarchy Entity" is a closed set, so it is enumerated rather than wildcarded.
    toTypes: ['Enterprise', 'OrganizationalUnit', 'ValueStream', 'ProductGroup', 'Product'],
    cardinality: 'N:1',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Span of control',
  },

  // Composition
  {
    code: 'REL-P001',
    category: 'Composition',
    type: 'INCLUDES_PROCESS',
    fromTypes: ['Product'],
    toTypes: ['Process'],
    cardinality: 'N:M',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Product boundary',
  },
  {
    code: 'REL-P002',
    category: 'Composition',
    type: 'INCLUDES_PROCESS_STEP',
    fromTypes: ['Product'],
    toTypes: ['ProcessStep'],
    cardinality: 'N:M',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Mapping studio',
  },
  {
    code: 'REL-P003',
    category: 'Composition',
    type: 'SUPPORTED_BY_APPLICATION',
    fromTypes: ['Product'],
    toTypes: ['Application'],
    cardinality: 'N:M',
    requiresRole: true,
    requiresEvidence: true,
    allowsAllocation: true,
    primaryUse: 'Product composition',
  },
  {
    code: 'REL-P004',
    category: 'Composition',
    type: 'SUPPORTED_BY_SERVICE',
    fromTypes: ['Product'],
    toTypes: ['Service'],
    cardinality: 'N:M',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: true,
    primaryUse: 'Product composition',
  },
  {
    code: 'REL-P005',
    category: 'Composition',
    type: 'CONSUMES_API',
    fromTypes: ['Product'],
    toTypes: ['API'],
    cardinality: 'N:M',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Dependency analysis',
  },
  {
    code: 'REL-P006',
    category: 'Composition',
    type: 'USES_DATA_ASSET',
    fromTypes: ['Product'],
    toTypes: ['DataAsset'],
    cardinality: 'N:M',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Data architecture',
  },
  {
    code: 'REL-P007',
    category: 'Composition',
    type: 'CONSUMES_DATA_PRODUCT',
    fromTypes: ['Product'],
    toTypes: ['DataProduct'],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Data product adoption',
  },
  {
    code: 'REL-P008',
    category: 'Composition',
    type: 'IMPLEMENTS_PROCESS',
    fromTypes: ['Application'],
    toTypes: ['Process'],
    cardinality: 'N:M',
    requiresRole: true,
    requiresEvidence: true,
    allowsAllocation: false,
    primaryUse: 'Application mapping',
  },
  {
    code: 'REL-P009',
    category: 'Composition',
    type: 'IMPLEMENTS_PROCESS_STEP',
    fromTypes: ['Application'],
    toTypes: ['ProcessStep'],
    cardinality: 'N:M',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Process mapping',
  },
  {
    code: 'REL-P010',
    category: 'Composition',
    type: 'EXPOSES_SERVICE',
    fromTypes: ['Application'],
    toTypes: ['Service'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Technology composition',
  },
  {
    code: 'REL-P011',
    category: 'Composition',
    type: 'EXPOSES_API',
    fromTypes: ['Service'],
    toTypes: ['API'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Interface inventory',
  },
  {
    code: 'REL-P012',
    category: 'Composition',
    type: 'STORES_OR_PROCESSES',
    fromTypes: ['Application'],
    toTypes: ['DataAsset'],
    cardinality: 'N:M',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Data dependency',
  },
  {
    code: 'REL-P013',
    category: 'Composition',
    type: 'BUILT_FROM',
    fromTypes: ['DataProduct'],
    toTypes: ['DataAsset'],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Data product composition',
  },
  {
    code: 'REL-P014',
    category: 'Composition',
    type: 'HAS_BOUNDARY',
    fromTypes: ['Product'],
    toTypes: ['ProductBoundary'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Governed boundary',
  },
  {
    code: 'REL-P015',
    category: 'Composition',
    type: 'INCLUDES_ENTITY',
    fromTypes: ['ProductBoundary'],
    // "Any allowed boundary entity" — the allowed set is tenant configuration.
    toTypes: [ANY_ENTITY],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: true,
    allowsAllocation: false,
    primaryUse: 'Boundary editor',
  },

  // Delivery
  {
    code: 'REL-D001',
    category: 'Delivery',
    type: 'ASSIGNED_TO_TEAM',
    fromTypes: ['WorkItem'],
    toTypes: ['Team'],
    cardinality: 'N:1',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Flow metrics',
  },
  {
    code: 'REL-D002',
    category: 'Delivery',
    type: 'ADVANCES_PRODUCT',
    fromTypes: ['WorkItem'],
    toTypes: ['Product'],
    cardinality: 'N:M',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: true,
    primaryUse: 'Outcome traceability',
  },
  {
    code: 'REL-D003',
    category: 'Delivery',
    type: 'TARGETS_OUTCOME',
    fromTypes: ['WorkItem'],
    toTypes: ['Outcome'],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: true,
    allowsAllocation: false,
    primaryUse: 'Value traceability',
  },
  {
    code: 'REL-D004',
    category: 'Delivery',
    type: 'DELIVERS_WORK_ITEM',
    fromTypes: ['Release'],
    toTypes: ['WorkItem'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Release flow',
  },
  {
    code: 'REL-D005',
    category: 'Delivery',
    type: 'RELEASES_PRODUCT',
    fromTypes: ['Release'],
    toTypes: ['Product'],
    cardinality: 'N:M',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Product delivery',
  },
  {
    code: 'REL-D006',
    category: 'Delivery',
    type: 'IMPLEMENTS_RELEASE',
    fromTypes: ['Deployment'],
    toTypes: ['Release'],
    cardinality: 'N:1',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Deployment metrics',
  },

  // Dependency
  {
    code: 'REL-X001',
    category: 'Dependency',
    type: 'DEPENDS_ON_PRODUCT',
    fromTypes: ['Product'],
    toTypes: ['Product'],
    cardinality: 'N:M',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Portfolio dependencies',
  },
  {
    code: 'REL-X002',
    category: 'Dependency',
    type: 'DEPENDS_ON_APPLICATION',
    fromTypes: ['Application'],
    toTypes: ['Application'],
    cardinality: 'N:M',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Architecture mapping',
  },
  {
    code: 'REL-X003',
    category: 'Dependency',
    type: 'DEPENDS_ON_SERVICE',
    fromTypes: ['Service'],
    toTypes: ['Service'],
    cardinality: 'N:M',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Reliability',
  },
  {
    code: 'REL-X004',
    category: 'Dependency',
    type: 'CALLS_API',
    fromTypes: ['API'],
    toTypes: ['API'],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Interface graph',
  },

  // Finance
  {
    code: 'REL-F001',
    category: 'Finance',
    type: 'FUNDS_ENTITY',
    fromTypes: ['FundingPlan'],
    toTypes: ['Product', 'ProductGroup', 'ValueStream'],
    cardinality: 'N:1',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Investment view',
  },
  {
    code: 'REL-F002',
    category: 'Finance',
    type: 'COST_OF_ENTITY',
    fromTypes: ['CostActual'],
    // "Any costable entity" — cost lands wherever the source booked it.
    toTypes: [ANY_ENTITY],
    cardinality: 'N:1',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Actual cost',
  },
  {
    code: 'REL-F003',
    category: 'Finance',
    type: 'ALLOCATES_COST_TO',
    fromTypes: ['CostAllocation'],
    toTypes: ['Product', 'ProductGroup', 'Service'],
    cardinality: 'N:1',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: true,
    primaryUse: 'Product costing',
  },
  {
    code: 'REL-F004',
    category: 'Finance',
    type: 'REALIZED_BY_PRODUCT',
    fromTypes: ['Benefit'],
    toTypes: ['Product'],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: true,
    primaryUse: 'Value realization',
  },
  {
    code: 'REL-F005',
    category: 'Finance',
    type: 'SUPPORTED_BY_RELEASE',
    fromTypes: ['Benefit'],
    toTypes: ['Release'],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: true,
    allowsAllocation: false,
    primaryUse: 'Delivery-to-value',
  },

  // Governance
  {
    code: 'REL-G001',
    category: 'Governance',
    type: 'SUPPORTS_ENTITY',
    fromTypes: ['Evidence'],
    toTypes: [ANY_ENTITY],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Explainability',
  },
  {
    code: 'REL-G002',
    category: 'Governance',
    type: 'DECIDES_ENTITY',
    fromTypes: ['Decision'],
    toTypes: [ANY_ENTITY],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Governance history',
  },
  {
    code: 'REL-G003',
    category: 'Governance',
    type: 'APPROVES_DECISION',
    fromTypes: ['Approval'],
    toTypes: ['Decision'],
    cardinality: 'N:1',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Approval timeline',
  },
  {
    code: 'REL-G004',
    category: 'Governance',
    type: 'FOLLOWS_FROM',
    fromTypes: ['ActionItem'],
    toTypes: ['Decision', 'Risk', 'DataQualityIssue'],
    cardinality: 'N:1',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Action management',
  },
  {
    code: 'REL-G005',
    category: 'Governance',
    type: 'COMMENTS_ON',
    fromTypes: ['Comment'],
    toTypes: [ANY_ENTITY],
    cardinality: 'N:1',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Collaboration',
  },

  // Hierarchy
  {
    code: 'REL-H001',
    category: 'Hierarchy',
    type: 'CONTAINS',
    fromTypes: ['Enterprise'],
    toTypes: ['OrganizationalUnit'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Portfolio roll-up',
  },
  {
    code: 'REL-H002',
    category: 'Hierarchy',
    type: 'CONTAINS',
    fromTypes: ['OrganizationalUnit'],
    toTypes: ['OrganizationalUnit'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Department hierarchy',
  },
  {
    code: 'REL-H003',
    category: 'Hierarchy',
    type: 'OWNS_VALUE_STREAM',
    fromTypes: ['OrganizationalUnit'],
    toTypes: ['ValueStream'],
    cardinality: '1:N',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Span of control',
  },
  {
    code: 'REL-H004',
    category: 'Hierarchy',
    type: 'CONTAINS_PRODUCT_GROUP',
    fromTypes: ['ValueStream'],
    toTypes: ['ProductGroup'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Portfolio decomposition',
  },
  {
    code: 'REL-H005',
    category: 'Hierarchy',
    type: 'CONTAINS_PRODUCT',
    fromTypes: ['ProductGroup'],
    toTypes: ['Product'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Product portfolio',
  },
  {
    code: 'REL-H006',
    category: 'Hierarchy',
    type: 'DIRECTLY_CONTAINS_PRODUCT',
    fromTypes: ['ValueStream'],
    toTypes: ['Product'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Simplified hierarchy',
  },
  {
    code: 'REL-H007',
    category: 'Hierarchy',
    type: 'HAS_SUBPRODUCT',
    fromTypes: ['Product'],
    toTypes: ['Product'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Composite products',
  },

  // Lineage
  {
    code: 'REL-L001',
    category: 'Lineage',
    type: 'PRODUCES_RUN',
    fromTypes: ['FileArtifact'],
    toTypes: ['IngestionRun'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'File history',
  },
  {
    code: 'REL-L002',
    category: 'Lineage',
    type: 'INGESTS_SOURCE_RECORD',
    fromTypes: ['IngestionRun'],
    toTypes: ['SourceRecord'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Lineage',
  },
  {
    code: 'REL-L003',
    category: 'Lineage',
    type: 'MAPS_TO_ENTITY',
    fromTypes: ['SourceRecord'],
    toTypes: [ANY_ENTITY],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Canonical provenance',
  },
  {
    code: 'REL-L004',
    category: 'Lineage',
    type: 'GENERATES_LINEAGE',
    fromTypes: ['MappingRule'],
    toTypes: ['LineageEdge'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Lineage workbench',
  },

  // Ownership
  {
    code: 'REL-O001',
    category: 'Ownership',
    type: 'OWNS_PRODUCT',
    fromTypes: ['Person'],
    toTypes: ['Product'],
    cardinality: '1:N',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Accountability',
  },
  {
    code: 'REL-O002',
    category: 'Ownership',
    type: 'OWNS_PROCESS',
    fromTypes: ['Person'],
    toTypes: ['Process'],
    cardinality: '1:N',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Process governance',
  },
  {
    code: 'REL-O003',
    category: 'Ownership',
    type: 'OWNS_APPLICATION',
    fromTypes: ['Person'],
    toTypes: ['Application'],
    cardinality: '1:N',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Application governance',
  },
  {
    code: 'REL-O004',
    category: 'Ownership',
    type: 'OWNS_DATA',
    fromTypes: ['Person'],
    toTypes: ['DataAsset', 'DataProduct'],
    cardinality: '1:N',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Data governance',
  },
  {
    code: 'REL-O005',
    category: 'Ownership',
    type: 'OWNS_METRIC',
    fromTypes: ['Person'],
    toTypes: ['MetricDefinition'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Metric governance',
  },
  {
    code: 'REL-O006',
    category: 'Ownership',
    type: 'DELIVERS_PRODUCT',
    fromTypes: ['Team'],
    toTypes: ['Product'],
    cardinality: 'N:M',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: true,
    primaryUse: 'Team topology',
  },
  {
    code: 'REL-O007',
    category: 'Ownership',
    type: 'MEMBER_OF_TEAM',
    fromTypes: ['Person'],
    toTypes: ['Team'],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Team membership',
  },

  // Quality
  {
    code: 'REL-L005',
    category: 'Quality',
    type: 'RAISES_ISSUE',
    fromTypes: ['DataQualityRule'],
    toTypes: ['DataQualityIssue'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'DQ management',
  },
  {
    code: 'REL-L006',
    category: 'Quality',
    type: 'AFFECTS_ENTITY',
    fromTypes: ['DataQualityIssue'],
    toTypes: [ANY_ENTITY],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'DQ workflow',
  },

  // Reliability
  {
    code: 'REL-D007',
    category: 'Reliability',
    type: 'IMPACTS_PRODUCT',
    fromTypes: ['Incident'],
    toTypes: ['Product'],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Product health',
  },
  {
    code: 'REL-D008',
    category: 'Reliability',
    type: 'IMPACTS_SERVICE',
    fromTypes: ['Incident'],
    toTypes: ['Service'],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Reliability',
  },
  {
    code: 'REL-D009',
    category: 'Reliability',
    type: 'CHANGES_APPLICATION',
    fromTypes: ['Change'],
    toTypes: ['Application'],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Change risk',
  },

  // Risk — same type name as REL-L006, different source endpoint.
  {
    code: 'REL-D010',
    category: 'Risk',
    type: 'AFFECTS_ENTITY',
    fromTypes: ['Risk'],
    toTypes: [ANY_ENTITY],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Risk views',
  },

  // Scenario
  {
    code: 'REL-C001',
    category: 'Scenario',
    type: 'HAS_ASSUMPTION',
    fromTypes: ['Scenario'],
    toTypes: ['ScenarioAssumption'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Scenario controls',
  },
  {
    code: 'REL-C002',
    category: 'Scenario',
    type: 'HAS_RESULT',
    fromTypes: ['Scenario'],
    toTypes: ['ScenarioResult'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Scenario output',
  },
  {
    code: 'REL-C003',
    category: 'Scenario',
    type: 'MODELS_ENTITY',
    fromTypes: ['Scenario'],
    toTypes: ['Product', 'ProductGroup', 'Enterprise'],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Scenario scope',
  },
  {
    code: 'REL-C004',
    category: 'Scenario',
    type: 'SELECTS_SCENARIO',
    fromTypes: ['Decision'],
    toTypes: ['Scenario'],
    cardinality: 'N:1',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Transformation decision',
  },

  // Sourcing
  {
    code: 'REL-S001',
    category: 'Sourcing',
    type: 'SUPPLIES_PRODUCT',
    fromTypes: ['Supplier'],
    toTypes: ['Product'],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Supplier view',
  },
  {
    code: 'REL-S002',
    category: 'Sourcing',
    type: 'GOVERNS_SUPPLIER',
    fromTypes: ['Contract'],
    toTypes: ['Supplier'],
    cardinality: 'N:1',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Commercial governance',
  },
  {
    code: 'REL-S003',
    category: 'Sourcing',
    type: 'SUPPORTS_PRODUCT',
    fromTypes: ['Contract'],
    toTypes: ['Product'],
    cardinality: 'N:M',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: true,
    primaryUse: 'Product 360',
  },
  {
    code: 'REL-S004',
    category: 'Sourcing',
    type: 'UNDER_CONTRACT',
    fromTypes: ['StatementOfWork'],
    toTypes: ['Contract'],
    cardinality: 'N:1',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'SOW management',
  },
  {
    code: 'REL-S005',
    category: 'Sourcing',
    type: 'DELIVERS_FOR_PRODUCT',
    fromTypes: ['StatementOfWork'],
    toTypes: ['Product'],
    cardinality: 'N:M',
    requiresRole: true,
    requiresEvidence: false,
    allowsAllocation: true,
    primaryUse: 'External delivery',
  },
  {
    code: 'REL-S006',
    category: 'Sourcing',
    type: 'HAS_OBLIGATION',
    fromTypes: ['Contract'],
    toTypes: ['ContractObligation'],
    cardinality: '1:N',
    requiresRole: false,
    requiresEvidence: false,
    allowsAllocation: false,
    primaryUse: 'Contract exposure',
  },
];

/** Every catalog row declaring this type. More than one for CONTAINS and AFFECTS_ENTITY. */
export function catalogEntriesFor(type: RelationshipType): RelationshipCatalogEntry[] {
  return RELATIONSHIP_CATALOG.filter((entry) => entry.type === type);
}

function endpointAllowed(allowed: readonly string[], entityType: string): boolean {
  return allowed.some((candidate) => candidate === ANY_ENTITY || candidate === entityType);
}

/**
 * Runtime guard behind validation rule RM-003 (invalid endpoint pair).
 *
 * A type is valid for a pair if *any* of its catalog rows accepts both ends.
 * That disjunction is the whole reason the catalog is keyed by REL- code rather
 * than by type name: `CONTAINS` legitimately joins Enterprise→OrganizationalUnit
 * and OrganizationalUnit→OrganizationalUnit, but it must not be allowed to join
 * Enterprise→Product by accidentally merging the two rows' endpoint sets.
 */
export function isValidEndpointPair(
  type: RelationshipType,
  fromEntityType: string,
  toEntityType: string,
): boolean {
  return RELATIONSHIP_CATALOG.some(
    (entry) =>
      entry.type === type &&
      endpointAllowed(entry.fromTypes, fromEntityType) &&
      endpointAllowed(entry.toTypes, toEntityType),
  );
}

// ── Validation rules ─────────────────────────────────────────────────────────

export interface RelationshipValidationRule {
  code: string;
  name: string;
  condition: string;
  /**
   * Where the catalog gives a range ("Error/Warning"), the strictest outcome is
   * recorded and the condition text says when policy may downgrade it. Erring
   * toward Error means a tenant has to opt into laxness deliberately.
   */
  severity: IssueSeverity;
}

/** Relationship Model §10, RM-001…RM-015. */
export const RELATIONSHIP_VALIDATION_RULES: readonly RelationshipValidationRule[] = [
  {
    code: 'RM-001',
    name: 'Prohibited self-loop',
    condition: 'fromEntityId equals toEntityId for prohibited types',
    severity: 'Error',
  },
  {
    code: 'RM-002',
    name: 'Hierarchy cycle',
    condition: 'New parent creates cycle',
    severity: 'Error',
  },
  {
    code: 'RM-003',
    name: 'Invalid endpoint pair',
    condition: 'Relationship type does not allow entity pair (see isValidEndpointPair)',
    severity: 'Error',
  },
  {
    code: 'RM-004',
    name: 'Invalid cardinality',
    condition: 'Active primary relationship exceeds allowed count',
    severity: 'Error',
  },
  {
    code: 'RM-005',
    name: 'Missing evidence',
    condition:
      'Relationship policy requires evidence; Warning where tenant policy makes evidence advisory',
    severity: 'Error',
  },
  {
    code: 'RM-006',
    name: 'Allocation over 100%',
    condition: 'Active allocations exceed permitted total',
    severity: 'Error',
  },
  {
    code: 'RM-007',
    name: 'Partial allocation',
    condition: 'Allocation below 100%; the unallocated balance is reported, never apportioned',
    severity: 'Warning',
  },
  {
    code: 'RM-008',
    name: 'Date overlap',
    condition: 'Exclusive effective relationships overlap',
    severity: 'Error',
  },
  {
    code: 'RM-009',
    name: 'Orphan application',
    condition: 'No product, platform or service relationship',
    severity: 'Warning',
  },
  {
    code: 'RM-010',
    name: 'Process overlap',
    condition:
      'Multiple primary product owners for same process scope; Warning while under steward review',
    severity: 'Error',
  },
  {
    code: 'RM-011',
    name: 'Stale source',
    condition: 'Evidence or source older than configured threshold',
    severity: 'Warning',
  },
  {
    code: 'RM-012',
    name: 'Source conflict',
    condition: 'Approved or current relationships conflict across sources; requires steward action',
    severity: 'Warning',
  },
  {
    code: 'RM-013',
    name: 'Unauthorized endpoint',
    condition: 'User lacks access to one endpoint; the message must not reveal restricted details',
    severity: 'Error',
  },
  {
    code: 'RM-014',
    name: 'Published mutation',
    condition: 'Attempt to edit a published relationship; create a new version instead',
    severity: 'Error',
  },
  {
    code: 'RM-015',
    name: 'Self-approval',
    condition: 'Proposer attempts approval under segregation policy',
    severity: 'Error',
  },
];

// ── Mapping sets ─────────────────────────────────────────────────────────────

/**
 * What a mapping set covers. Held explicitly because approval publishes a set,
 * not a screen's worth of edges — a reviewer has to be able to see the boundary
 * of what they are approving before they approve it.
 */
export const MappingSetScope = z.object({
  domain: z.string().nullable(),
  hierarchyNodeId: Uuid.nullable(),
  entityTypes: z.array(EntityType),
  selectionCriteria: z.string().nullable(),
});
export type MappingSetScope = z.infer<typeof MappingSetScope>;

/**
 * A governed release of relationships (Relationship Model §6).
 *
 * Version is part of identity, not metadata: a published set is immutable, so
 * "change" means cloning to a new version and superseding the old one. That is
 * what makes an as-of query reproducible after the mapping has moved on.
 */
export const MappingSet = z.object({
  id: Uuid,
  name: z.string().min(1),
  scope: MappingSetScope,
  version: z.number().int().positive(),
  status: MappingSetStatus,

  createdBy: Uuid,
  createdAt: Timestamp,
  submittedBy: Uuid.nullable(),
  submittedAt: Timestamp.nullable(),
  approvedBy: Uuid.nullable(),
  approvedAt: Timestamp.nullable(),
  publishedAt: Timestamp.nullable(),

  /** Set on the older version when a newer one is published. Null while current. */
  supersededByVersion: z.number().int().positive().nullable(),
});
export type MappingSet = z.infer<typeof MappingSet>;
